import {
  booleanFieldContentSchema,
  type Field,
  type BooleanField,
  type VariableTextField,
  type DateField,
  FieldType,
  fieldSchema,
  IdentityField,
  dateFieldSchema,
  identityFieldSchema,
  variableTextFieldSchema,
  MultipleChoiceField,
  multipleChoiceFieldSchema,
} from './types';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { renderIdentity, renderDate, renderTextField } from './renderers';

import type { Delta } from 'quill/core';
import { z } from 'zod';
import { ServerDelta } from './server-delta';
import { pdfStyles } from './styles';
import { serializeStyles } from '@emotion/serialize';

const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const templateToDocument = (template: Delta, fields: z.infer<typeof fieldSchema>[]): Delta => {
  const document = new ServerDelta();

  template.ops.forEach(o => {
    const op = deepClone(o);

    if (op.attributes?.[FieldType.BOOLEAN]) {
      const id = (op.attributes[FieldType.BOOLEAN] as Field).id;
      const value = fields.find(v => v.id === id)?.value.content;

      const parsed = booleanFieldContentSchema.parse(value ?? false);

      const shouldBeShown = (op.attributes[FieldType.BOOLEAN] as BooleanField).reverse ? !parsed : parsed;

      if (!shouldBeShown) {
        return;
      }
    }

    if (op.insert && typeof op.insert === 'object' && FieldType.VARIABLE_TEXT in op.insert) {
      const variable = op.insert[FieldType.VARIABLE_TEXT] as VariableTextField;
      const id = variable.id;

      const field = fields.find(v => v.id === id);

      if (field?.value) {
        const parsed = variableTextFieldSchema.parse(field);
        variable.value = parsed.value;
      }
    }

    if (op.insert && typeof op.insert === 'object' && FieldType.MULTIPLE_CHOICE in op.insert) {
      const variable = op.insert[FieldType.MULTIPLE_CHOICE] as MultipleChoiceField;
      const id = variable.id;

      const field = fields.find(v => v.id === id);

      if (field?.value) {
        const parsed = multipleChoiceFieldSchema.parse(field);
        variable.value = parsed.value;
      }
    }

    if (op.insert && typeof op.insert === 'object' && FieldType.DATE in op.insert) {
      const variable = op.insert[FieldType.DATE] as DateField;
      const id = variable.id;

      const field = fields.find(v => v.id === id);

      if (field?.value) {
        const parsed = dateFieldSchema.parse(field);
        variable.value = parsed.value;
      }
    }

    if (op.insert && typeof op.insert === 'object' && FieldType.IDENTITY in op.insert) {
      const variable = op.insert[FieldType.IDENTITY] as IdentityField;
      const id = variable.id;

      const field = fields.find(v => v.id === id);

      if (field?.value) {
        const parsed = identityFieldSchema.parse(field);
        variable.value = parsed.value;
      }
    }

    document.ops.push(op);
  });

  return document;
};

export const documentIsInValid = (template: Delta, fields: z.infer<typeof fieldSchema>[]): boolean => {
  const document = templateToDocument(template, fields);
  return document.ops.some(op => {
    if (op.insert && typeof op.insert === 'object') {
      const key = Object.values(FieldType).find(v => v in (op.insert as Record<string, unknown>)) as
        | FieldType
        | undefined;

      if (!key) {
        return false;
      }

      const variable = op.insert[key] as Field;

      return !variable.value;
    }
    return false;
  });
};

export const agreementToHtml = (...args: Parameters<typeof templateToDocument>): string => {
  const doc = templateToDocument(...args);
  const converter = new QuillDeltaToHtmlConverter(doc.ops);
  converter.renderCustomWith(function (customOp, _contextOp) {
    const type = customOp.insert.type as FieldType;
    switch (type) {
      case FieldType.BOOLEAN:
        throw new Error('Boolean fieldsare not handled here');
      case FieldType.IDENTITY:
        return renderIdentity(customOp.insert.value.value.content);
      case FieldType.DATE:
        return renderDate(customOp.insert.value.value.content);
      case FieldType.VARIABLE_TEXT:
      case FieldType.MULTIPLE_CHOICE:
        return renderTextField(customOp.insert.value.value.content);
      default: {
        const exhaustiveCheck: never = type;
        throw new Error(`Unhandled type case: ${exhaustiveCheck}`);
      }
    }
  });

  let html = converter.convert();
  const cssString = serializeStyles([pdfStyles]).styles;

  html += `<style>${cssString}</style>`;

  return html;
};

export const hasFieldWithId = (template: Delta, fieldId: string): boolean => {
  return template.ops.some(op => {
    if (op.insert && typeof op.insert === 'object') {
      const field = Object.values(FieldType).find(fieldType => {
        const fieldData = op.insert as Record<string, unknown>;
        const field = fieldData ? (fieldData[fieldType as string] as Field | undefined) : undefined;
        return field?.id === fieldId;
      });

      return !!field;
    }
    return false;
  });
};
